import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Clock, Warning } from "@phosphor-icons/react";

// Auto-logout after inactivity, with two warning stages before it.
// Testing overrides (ms): localStorage 'sx_idle_ms', 'sx_warn1_ms', 'sx_warn2_ms'.
const DEFAULT_IDLE_MS = 30 * 60 * 1000; // 30 minutes
const DEFAULT_WARN1_MS = 5 * 60 * 1000; // first warning 5 min before logout
const DEFAULT_WARN2_MS = 2 * 60 * 1000; // final warning 2 min before logout
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];

const fmt = (s) => {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
};

const num = (k) => {
  const v = parseInt(localStorage.getItem(k) || "", 10);
  return Number.isFinite(v) && v > 0 ? v : null;
};

export default function InactivityLock() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [stage, setStage] = useState(0); // 0 none, 1 warn(5m), 2 final(2m)
  const [remaining, setRemaining] = useState(0);
  const lastActivity = useRef(Date.now());
  const loggingOut = useRef(false);

  const idleMs = () => num("sx_idle_ms") || DEFAULT_IDLE_MS;
  const warn1Ms = () => {
    const w = num("sx_warn1_ms") || DEFAULT_WARN1_MS;
    return Math.min(w, idleMs() - 1000);
  };
  const warn2Ms = () => {
    const w = num("sx_warn2_ms") || DEFAULT_WARN2_MS;
    return Math.min(w, warn1Ms() - 1000);
  };

  const onActivity = useCallback(() => {
    if (!loggingOut.current) lastActivity.current = Date.now();
  }, []);

  const staySignedIn = () => {
    lastActivity.current = Date.now();
    setStage(0);
  };

  const doLogout = useCallback(async () => {
    if (loggingOut.current) return;
    loggingOut.current = true;
    await logout();
    navigate("/login");
  }, [logout, navigate]);

  useEffect(() => {
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    const interval = setInterval(() => {
      if (loggingOut.current) return;
      const total = idleMs();
      const rem = total - (Date.now() - lastActivity.current);
      if (rem <= 0) {
        setStage(0);
        doLogout();
        return;
      }
      setRemaining(Math.max(0, Math.ceil(rem / 1000)));
      if (rem <= warn2Ms()) setStage(2);
      else if (rem <= warn1Ms()) setStage(1);
      else setStage(0);
    }, 1000);
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onActivity, doLogout]);

  if (stage === 0) return null;

  const final = stage === 2;
  const accent = final ? "#ef4444" : "#f59e0b";

  return (
    <div
      data-testid="inactivity-warning"
      data-stage={stage}
      className="fixed bottom-5 right-5 z-[90] w-[340px] rounded-lg bg-[#0b1220] shadow-2xl p-4 fade-up"
      style={{ border: `1px solid ${accent}66` }}
    >
      <div className="flex items-center gap-2 mb-1.5" style={{ color: accent }}>
        {final ? <Warning size={18} weight="fill" /> : <Clock size={18} weight="fill" />}
        <span className="overline">{final ? "Final warning" : "Inactivity warning"}</span>
      </div>
      <p className="text-sm text-slate-300">
        You'll be signed out in{" "}
        <span data-testid="inactivity-warning-countdown" className="font-mono font-semibold" style={{ color: accent }}>
          {fmt(remaining)}
        </span>{" "}
        due to inactivity.
      </p>
      <Button
        data-testid="stay-signed-in-button"
        onClick={staySignedIn}
        className="mt-3 w-full bg-blue-600 hover:bg-blue-500 text-white h-9"
      >
        Stay signed in
      </Button>
    </div>
  );
}
